import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { Button, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { EMPTY_VALUE_PLACEHOLDER, formatDecimal, formatShortDate } from "@shared";

import { SrvProceedingClaimsSnapshotResponse } from "@/api";
import { useTranslation } from "@/translations";

const TRANSLATION_BASE = "clients.client.debtSettlement.srvProceeding.claimsSnapshot";
const COLUMN_COUNT = 4;

interface Props {
  snapshot: SrvProceedingClaimsSnapshotResponse;
  onRefresh: () => void;
  isRefreshing: boolean;
}

/** The claims as they stood when the Verfahren was created or last refreshed; read-only. */
export function ClaimsSnapshotSection({ snapshot, onRefresh, isRefreshing }: Props) {
  const { t, i18n } = useTranslation();

  return (
    <Stack gap={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
        <Typography variant="h5">{t("clients.client.debtSettlement.srvProceeding.sections.claimsSnapshot")}</Typography>
        {snapshot.isStale && (
          <Stack direction="row" alignItems="center" gap={2}>
            <Typography variant="body2" color="text.secondary">
              {t(`${TRANSLATION_BASE}.refreshHint`)}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshRoundedIcon />}
              loading={isRefreshing}
              onClick={onRefresh}
            >
              {t(`${TRANSLATION_BASE}.refresh`)}
            </Button>
          </Stack>
        )}
      </Stack>
      <Table size="small" aria-label={t(`${TRANSLATION_BASE}.caption`)}>
        <TableHead>
          <TableRow>
            <TableCell>{t(`${TRANSLATION_BASE}.creditor`)}</TableCell>
            <TableCell>{t(`${TRANSLATION_BASE}.caseNumber`)}</TableCell>
            <TableCell>{t(`${TRANSLATION_BASE}.registeredOn`)}</TableCell>
            <TableCell align="right">{t(`${TRANSLATION_BASE}.amount`)}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {snapshot.rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={COLUMN_COUNT}>
                <Typography variant="body2" color="text.secondary">
                  {t(`${TRANSLATION_BASE}.empty`)}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            snapshot.rows.map((row) => (
              <TableRow key={row.claimId}>
                <TableCell component="th">{row.creditorName}</TableCell>
                <TableCell>{row.caseNumber ?? EMPTY_VALUE_PLACEHOLDER}</TableCell>
                <TableCell>{row.registeredOn ? formatShortDate(row.registeredOn) : EMPTY_VALUE_PLACEHOLDER}</TableCell>
                <TableCell align="right">{formatDecimal(row.amount, i18n.language)}</TableCell>
              </TableRow>
            ))
          )}
          <TableRow>
            <TableCell component="th" colSpan={COLUMN_COUNT - 1}>
              <Typography fontWeight="bold">{t(`${TRANSLATION_BASE}.total`)}</Typography>
            </TableCell>
            <TableCell align="right">
              <Typography fontWeight="bold">{formatDecimal(snapshot.total, i18n.language)}</Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Stack>
  );
}
